import { useEffect, useState } from 'react';
import { apiCall, default as API_CONFIG } from '../config/api';

/** Module-level caches so list <-> detail navigation doesn't refetch. */
let _tickerListCache = null;
let _themeListCache = null;

export const useTickers = (limit = 200) => {
  const [tickers, setTickers] = useState(_tickerListCache || []);
  const [loading, setLoading] = useState(!_tickerListCache);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (_tickerListCache) return;
    let cancelled = false;
    setLoading(true);
    apiCall(API_CONFIG.endpoints.tickersWithLimit(limit))
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        _tickerListCache = list;
        setTickers(list);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load tickers', err);
        setError('Failed to load tickers');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [limit]);

  return { tickers, loading, error };
};

export const useThemes = (limit = 200) => {
  const [themes, setThemes] = useState(_themeListCache || []);
  const [loading, setLoading] = useState(!_themeListCache);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (_themeListCache) return;
    let cancelled = false;
    setLoading(true);
    apiCall(API_CONFIG.endpoints.themesWithLimit(limit))
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        _themeListCache = list;
        setThemes(list);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load themes', err);
        setError('Failed to load themes');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [limit]);

  return { themes, loading, error };
};

export const useTicker = (symbol) => useDetail(symbol, API_CONFIG.endpoints.tickerById);
export const useTheme  = (slug)   => useDetail(slug,   API_CONFIG.endpoints.themeBySlug);

function useDetail(key, urlBuilder) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!key) {
      setItem(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiCall(urlBuilder(key))
      .then((data) => { if (!cancelled) setItem(data); })
      .catch((err) => {
        if (cancelled) return;
        const msg = (err?.message || '').toLowerCase();
        setError(msg.includes('not found') || msg.includes('404') ? 'not-found' : 'Failed to load detail');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { item, loading, error };
}
